import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { ReportHero } from '../components/MonthlyReport/ReportHero';
import { PatternCard } from '../components/MonthlyReport/PatternCard';
import { TrendSection } from '../components/MonthlyReport/TrendSection';
import { InsightCard } from '../components/MonthlyReport/InsightCard';
import { useAuth } from '../contexts/AuthContext';
import { useScans } from '../hooks/useScans';
import { Scan, Pattern } from '../types/scan';
import { ChevronDown, Lock, Crown, Zap } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { SubscriptionModal } from '../components/Settings/SubscriptionModal';
export function MonthlyReportPage() {
  const {
    user
  } = useAuth();
  const {
    scans,
    loading
  } = useScans(user?.id);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  // Reliable pro status from DB
  const [profile, setProfile] = useState<{
    is_pro: boolean;
    scan_credits: number;
  } | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const isPro = profile?.is_pro || false;
  useEffect(() => {
    async function fetchProfile() {
      if (!user?.id) return;
      try {
        const {
          data,
          error
        } = await supabase.from('profiles').select('is_pro, scan_credits').eq('id', user.id).single();
        if (error) throw error;
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoadingProfile(false);
      }
    }
    fetchProfile();
    // Real-time subscription for profile updates
    const channel = supabase.channel('profile-monthly-report-updates').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'profiles',
      filter: `id=eq.${user?.id}`
    }, payload => {
      const newProfile = payload.new as {
        is_pro: boolean;
        scan_credits: number;
      };
      setProfile(newProfile);
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);
  // Calculate monthly stats
  const stats = useMemo(() => {
    if (!scans || scans.length === 0) return null;
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    // Filter for current month scans
    const monthlyScans = scans.filter(s => {
      const d = new Date(s.created_at);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear && s.status === 'completed';
    });
    // Filter for previous month scans
    const prevMonthScans = scans.filter(s => {
      const d = new Date(s.created_at);
      const isPrevMonth = currentMonth === 0 ? d.getMonth() === 11 && d.getFullYear() === currentYear - 1 : d.getMonth() === currentMonth - 1 && d.getFullYear() === currentYear;
      return isPrevMonth && s.status === 'completed';
    });
    // Calculate scores
    const avgScore = monthlyScans.length > 0 ? monthlyScans.reduce((sum, s) => sum + (s.score || 0), 0) / monthlyScans.length : 0;
    const prevAvgScore = prevMonthScans.length > 0 ? prevMonthScans.reduce((sum, s) => sum + (s.score || 0), 0) / prevMonthScans.length : 0;
    // Aggregate patterns
    const patternCounts: Record<string, number> = {};
    monthlyScans.forEach(scan => {
      // Add type guard to ensure patterns is an array
      if (Array.isArray(scan.patterns)) {
        scan.patterns.forEach(p => {
          patternCounts[p.type] = (patternCounts[p.type] || 0) + 1;
        });
      }
    });
    const prevPatternCounts: Record<string, number> = {};
    prevMonthScans.forEach(scan => {
      // Add type guard to ensure patterns is an array
      if (Array.isArray(scan.patterns)) {
        scan.patterns.forEach(p => {
          prevPatternCounts[p.type] = (prevPatternCounts[p.type] || 0) + 1;
        });
      }
    });
    // Sort patterns by frequency
    const topPatterns = Object.entries(patternCounts).sort(([, a], [, b]) => b - a).slice(0, 3).map(([type, count]) => {
      const prevCount = prevPatternCounts[type] || 0;
      // Normalize by number of scans to get accurate trend
      const currentFreq = count / (monthlyScans.length || 1);
      const prevFreq = prevCount / (prevMonthScans.length || 1);
      let trend: 'up' | 'down' | 'stable' = 'stable';
      if (currentFreq > prevFreq * 1.1) trend = 'up';
      if (currentFreq < prevFreq * 0.9) trend = 'down';
      return {
        type,
        count,
        trend
      };
    });
    // Categorize trends for all patterns
    const improved = [];
    const stable = [];
    const worsened = [];
    const allTypes = new Set([...Object.keys(patternCounts), ...Object.keys(prevPatternCounts)]);
    allTypes.forEach(type => {
      const currentCount = patternCounts[type] || 0;
      const prevCount = prevPatternCounts[type] || 0;
      // Calculate percentage change
      const diff = currentCount - prevCount;
      const percentChange = prevCount > 0 ? Math.round(diff / prevCount * 100) : 100;
      if (diff < 0) improved.push({
        type,
        count: currentCount,
        change: percentChange
      });else if (diff > 0) worsened.push({
        type,
        count: currentCount,
        change: percentChange
      });else stable.push({
        type,
        count: currentCount,
        change: 0
      });
    });
    return {
      monthName: now.toLocaleString('default', {
        month: 'long'
      }),
      year: currentYear,
      avgScore,
      prevAvgScore,
      topPatterns,
      trends: {
        improved,
        stable,
        worsened
      },
      scanCount: monthlyScans.length
    };
  }, [scans]);
  if (loading || loadingProfile) {
    return <div className="min-h-screen bg-gray-950 flex items-center justify-center" data-id="element-9111" data-mp="3dfgi3wvdhkik">
        <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" data-id="element-9112" data-mp="39ewnazy9uszl" />
      </div>;
  }
  if (!stats || stats.scanCount === 0) {
    return <div className="min-h-screen bg-gray-950 text-white flex flex-col" data-id="element-9113" data-mp="2gr0o9ol8cozn">
        <WebsiteHeader data-id="element-9114" data-mp="2uq8rcskikh94" />
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-9115" data-mp="2uqs1vo7u43d4">
          <h1 className="text-4xl font-bold mb-4" data-id="element-9116" data-mp="2pqntymkzsgyi">No Data Yet</h1>
          <p className="text-gray-400 mb-8 max-w-md" data-id="element-9117" data-mp="2pt9x8a0uoaax">
            Upload some chat logs to generate your first monthly communication
            report.
          </p>
          <a href="/upload" className="px-6 py-3 bg-purple-600 rounded-full font-bold hover:bg-purple-700 transition-colors" data-id="element-9118" data-mp="50558zq19pjg">
            Start a Scan
          </a>
        </div>
      </div>;
  }
  // Pro gate: free users see limited report with upgrade CTA
  if (!isPro) {
    return <div className="min-h-screen bg-gray-950 text-white overflow-x-hidden selection:bg-purple-500/30" data-id="element-9119" data-mp="3lvqvgtigvsx">
        <div className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-md border-b border-white/5" data-id="element-9120" data-mp="8kp4racpmotm">
          <WebsiteHeader data-id="element-9121" data-mp="117jkjtyl100r" />
        </div>

        <main className="pt-20" data-id="element-9122" data-mp="116i1p3bbr98h">
          {/* Show hero with blurred/limited data */}
          <ReportHero month={stats.monthName} year={stats.year} score={stats.avgScore} previousScore={stats.prevAvgScore} data-id="element-9123" data-mp="mjf0omnsmige" />

          {/* Upgrade Gate Overlay */}
          <section className="max-w-2xl mx-auto px-4 py-20" data-id="element-9124" data-mp="1f5q0vw7hmhco">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="relative bg-[#0a0a0a] border-2 border-[#FFD700]/30 p-8 md:p-12 text-center overflow-hidden" data-id="element-9125" data-mp="1jyj60l6vbrpa">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#FFD700]" data-id="element-9126" data-mp="32blq9b3kzq70" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#FFD700]" data-id="element-9127" data-mp="2sjgq3pcbdzb" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#FFD700]" data-id="element-9128" data-mp="3tyu9lck758fk" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#FFD700]" data-id="element-9129" data-mp="3q16d452uv7lk" />

              {/* Scanning Line */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent animate-[scan_4s_ease-in-out_infinite]" data-id="element-9130" data-mp="2ekgcd1h7b7zc" />

              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFD700]/10 border border-[#FFD700] rounded-full mb-6" data-id="element-9131" data-mp="p3b3dmzbmt41">
                <Lock className="w-8 h-8 text-[#FFD700]" data-id="element-9132" data-mp="u2xftu7bs5bs" />
              </div>

              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-9133" data-mp="y3hc6vk2r4wv">
                <Crown className="w-5 h-5 text-[#FFD700]" data-id="element-9134" data-mp="12u7hk8si37rk" />
                <span className="text-[#FFD700] font-bold text-xs tracking-widest uppercase font-mono" data-id="element-9135" data-mp="12vqz02pw7oux">
                  Pro Feature
                </span>
              </div>

              <h2 className="text-2xl md:text-3xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9136" data-mp="1crkgm0igy8jk">
                Monthly Clarity Report
              </h2>

              <p className="text-gray-400 mb-4 max-w-md mx-auto font-mono text-sm leading-relaxed" data-id="element-9137" data-mp="1lt1y9nbiyiad">
                Your report has{' '}
                <span className="text-white font-bold" data-id="element-9138" data-mp="3jkemel2x82w7">
                  {stats.scanCount} scans
                </span>{' '}
                analyzed with{' '}
                <span className="text-white font-bold" data-id="element-9139" data-mp="68i2ldiw2v91">
                  {stats.topPatterns.length} patterns
                </span>{' '}
                detected this month. Upgrade to Pro to unlock the full
                breakdown.
              </p>

              {/* Teaser stats */}
              <div className="grid grid-cols-3 gap-4 mb-8 max-w-sm mx-auto" data-id="element-9140" data-mp="k7zwg7b1kyp9">
                <div className="bg-gray-800/50 border border-gray-700 p-3" data-id="element-9141" data-mp="k5wtkmosjb34">
                  <div className="text-xl font-black text-white" data-id="element-9142" data-mp="p6kwo6qanidp">
                    {stats.scanCount}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-9143" data-mp="t6bhzux1x6au">
                    Scans
                  </div>
                </div>
                <div className="bg-gray-800/50 border border-gray-700 p-3" data-id="element-9144" data-mp="12xzfodk7bjkr">
                  <div className="text-xl font-black text-white" data-id="element-9145" data-mp="17ydgbajh3r8r">
                    {stats.topPatterns.length}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-9146" data-mp="1cwwzx4algqxy">
                    Patterns
                  </div>
                </div>
                <div className="bg-gray-800/50 border border-gray-700 p-3" data-id="element-9147" data-mp="2selbeondly8d">
                  <div className="text-xl font-black text-white" data-id="element-9148" data-mp="2ng06wd13cse4">
                    {Math.round(stats.avgScore)}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-9149" data-mp="2ij018385lrnj">
                    Avg Score
                  </div>
                </div>
              </div>

              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => setShowSubscriptionModal(true)} className="inline-flex items-center gap-2 bg-[#FFD700] text-black px-8 py-4 font-black uppercase tracking-wider hover:bg-[#FFD700]/90 transition-all shadow-[0_0_20px_rgba(255,215,0,0.4)]" data-id="element-9150" data-mp="3b6mfc5f0ceon">
                <Zap className="w-5 h-5 fill-black" data-id="element-9151" data-mp="1s76bfs14xgbk" />
                Unlock Full Report
              </motion.button>

              <p className="text-xs text-gray-600 font-mono mt-4" data-id="element-9152" data-mp="1x57w2he7lnp5">
                Pro members get unlimited monthly reports with full trend
                analysis
              </p>
            </motion.div>
          </section>
        </main>

        <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} data-id="element-9153" data-mp="1ned8n9p88i24" />
      </div>;
  }
  return <div className="min-h-screen bg-gray-950 text-white overflow-x-hidden selection:bg-purple-500/30" data-id="element-9154" data-mp="1dk3g9s0abjre">
      <div className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-md border-b border-white/5" data-id="element-9155" data-mp="zkbxldytpf72">
        <WebsiteHeader data-id="element-9156" data-mp="14jy8hgr6ijcp" />
      </div>

      <main className="pt-20" data-id="element-9157" data-mp="2twa4h99ntyzs">
        {/* Hero Section */}
        <ReportHero month={stats.monthName} year={stats.year} score={stats.avgScore} previousScore={stats.prevAvgScore} data-id="element-9158" data-mp="2tup8rgbm0a0c" />

        {/* Scroll Indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1,
        y: [0, 10, 0]
      }} transition={{
        delay: 3,
        duration: 2,
        repeat: Infinity
      }} className="flex justify-center mb-20" data-id="element-9159" data-mp="n6f7w95gvsei">
          <ChevronDown className="text-gray-500" data-id="element-9160" data-mp="15yhpbmpxncpt" />
        </motion.div>

        {/* Top Patterns Section */}
        <section className="max-w-6xl mx-auto px-4 mb-32" data-id="element-9161" data-mp="110pm3wju846b">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-9162" data-mp="112agnm65jn46">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-200 to-blue-200" data-id="element-9163" data-mp="3cpqm5cnbpll2">
              Recurring Themes
            </h2>
            <p className="text-gray-400" data-id="element-9164" data-mp="28rmxpqqejqtr">
              The patterns that defined your conversations this month
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9165" data-mp="2ds18hgje1evh">
            {stats.topPatterns.map((pattern, idx) => <PatternCard key={pattern.type} type={pattern.type} count={pattern.count} trend={pattern.trend} rank={idx + 1} delay={idx * 0.2} data-id="element-9166" data-mp="2hrice5j623yj" data-mpi={idx} />)}
          </div>
        </section>

        {/* Trends Section */}
        <section className="bg-gray-900/50 py-24 mb-24 border-y border-white/5" data-id="element-9167" data-mp="1yt75tljjwdj7">
          <div className="max-w-6xl mx-auto px-4 mb-16 text-center" data-id="element-9168" data-mp="1yva751q5lt38">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-9169" data-mp="12b1fsjt4oy0x">
              Trend Analysis
            </h2>
            <p className="text-gray-400" data-id="element-9170" data-mp="16bboi48b22jh">
              How your communication patterns are shifting
            </p>
          </div>

          <TrendSection improved={stats.trends.improved} stable={stats.trends.stable} worsened={stats.trends.worsened} data-id="element-9171" data-mp="tusdlwz4y1q8" />
        </section>

        {/* Insight Section */}
        <section className="max-w-4xl mx-auto px-4 pb-32" data-id="element-9172" data-mp="kudu935qewpd">
          <InsightCard insight={stats.avgScore < 40 ? "You've maintained healthy boundaries this month. Continue practicing active listening to deepen your connections." : stats.avgScore < 70 ? 'We noticed some mixed signals. Try to pause before responding when you feel defensive.' : 'High tension detected. Consider setting clearer boundaries or taking breaks from heated conversations.'} actionLink="/blog" actionText="Explore Communication Tips" data-id="element-9173" data-mp="kw6zce0p58v9" />
        </section>
      </main>
    </div>;
}